import type { Query } from "../../api/client";

/**
 * Filter state lives in the URL. That is what makes Telegram's back button, our
 * own history, and a re-opened deep link all agree without a store to sync.
 */
export interface Filters {
  q: string;
  type: string | null;
  subtype: string | null;
  artist: string | null;
  style: string | null;
  ownership_status: string | null;
  condition_status: string | null;
  medium: string[];
  year_from: number | null;
  year_to: number | null;
  year_unknown: boolean;
  has_primary_media: boolean | null;
}

export const emptyFilters: Filters = {
  q: "",
  type: null,
  subtype: null,
  artist: null,
  style: null,
  ownership_status: null,
  condition_status: null,
  medium: [],
  year_from: null,
  year_to: null,
  year_unknown: false,
  has_primary_media: null,
};

const SINGLE = [
  "type",
  "subtype",
  "artist",
  "style",
  "ownership_status",
  "condition_status",
] as const;

export function filtersFromParams(params: URLSearchParams): Filters {
  const number = (key: string) => {
    const raw = params.get(key);
    const value = raw === null ? Number.NaN : Number(raw);
    return Number.isFinite(value) ? value : null;
  };

  const filters: Filters = {
    ...emptyFilters,
    q: params.get("q") ?? "",
    medium: params.getAll("medium").filter(Boolean),
    year_from: number("year_from"),
    year_to: number("year_to"),
    year_unknown: params.get("year_unknown") === "true",
    has_primary_media:
      params.get("has_primary_media") === null ? null : params.get("has_primary_media") === "true",
  };
  for (const key of SINGLE) filters[key] = params.get(key) || null;
  return filters;
}

export function filtersToParams(filters: Filters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.q) params.set("q", filters.q);
  for (const key of SINGLE) {
    if (filters[key]) params.set(key, filters[key]!);
  }
  for (const id of filters.medium) params.append("medium", id);
  if (filters.year_from !== null) params.set("year_from", String(filters.year_from));
  if (filters.year_to !== null) params.set("year_to", String(filters.year_to));
  if (filters.year_unknown) params.set("year_unknown", "true");
  if (filters.has_primary_media !== null) {
    params.set("has_primary_media", String(filters.has_primary_media));
  }
  return params;
}

/** Exactly the query keys catalog/filters.py accepts. */
export function filtersToQuery(filters: Filters): Query {
  return {
    q: filters.q || undefined,
    type: filters.type ?? undefined,
    subtype: filters.subtype ?? undefined,
    artist: filters.artist ?? undefined,
    style: filters.style ?? undefined,
    ownership_status: filters.ownership_status ?? undefined,
    condition_status: filters.condition_status ?? undefined,
    medium: filters.medium.length ? filters.medium : undefined,
    year_from: filters.year_from ?? undefined,
    year_to: filters.year_to ?? undefined,
    year_unknown: filters.year_unknown ? true : undefined,
    has_primary_media: filters.has_primary_media ?? undefined,
  };
}

/** How many chips the filter button should badge. `q` is the search box, not a filter. */
export function activeFilterCount(filters: Filters): number {
  let count = 0;
  for (const key of SINGLE) if (filters[key]) count += 1;
  count += filters.medium.length;
  if (filters.year_from !== null || filters.year_to !== null) count += 1;
  if (filters.year_unknown) count += 1;
  if (filters.has_primary_media !== null) count += 1;
  return count;
}

export function hasAnyFilter(filters: Filters): boolean {
  return activeFilterCount(filters) > 0 || filters.q.length > 0;
}
